import * as Yup from 'yup'
import { WriterEnum } from '@interfaces/WriterEnum'
import { ResidenceEnum } from '@interfaces/ResidenceEnum'

export const validationSchema = Yup.object().shape({
  type: Yup.string().required(),
  affiliation: Yup.string().required(),
  fullName: Yup.string().when('type', {
    is: WriterEnum.INDIVIDUAL,
    then: Yup.string().required(),
  }),
  idNumber: Yup.string().when('type', {
    is: WriterEnum.INDIVIDUAL,
    then: Yup.string()
      .matches(/^\d+$/)
      .min(13)
      .max(13)
      .required()
      .test('verify-id-number', 'idNumber-is-ban', (value, context) => {
        return context.parent.validateNumberId
      }),
  }),
  email: Yup.string().email().when('type', {
    is: WriterEnum.INDIVIDUAL,
    then: Yup.string().email().required(),
  }),
  phoneNumber: Yup.string().when('type', {
    is: WriterEnum.INDIVIDUAL,
    then: Yup.string().matches(/^\d+$/).min(9).max(10).required(),
  }),
  companyName: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().required(),
  }),
  taxId: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string()
      .matches(/^\d+$/)
      .min(13)
      .max(13)
      .required()
      .test('verify-id-number', 'taxId-is-ban', (value, context) => {
        return context.parent.validateTaxId
      }),
  }),
  companyCertificateImgPath: Yup.object().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.object().shape({
      url: Yup.string().required(),
    }),
  }),
  coordinatorFullName: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().required(),
  }),
  coordinatorEmail: Yup.string().email().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().email().required(),
  }),
  coordinatorPhoneNumber: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().matches(/^\d+$/).min(9).max(10).required(),
  }),
  accountingFullName: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().required(),
  }),
  accountingEmail: Yup.string().email().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().email().required(),
  }),
  accountingPhoneNumber: Yup.string().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.string().matches(/^\d+$/).min(9).max(10).required(),
  }),
  residence: Yup.string().required(),
  country: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().required(),
  }),
  address: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  postcode: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  province: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  district: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  subDistrict: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  idCardImgPath: Yup.object().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.LOCAL,
    then: Yup.object().shape({
      url: Yup.string().required(),
    }),
  }),
  aboardAddress: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().required(),
  }),
  aboardPostcode: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().matches(/^\d+$/).required(),
  }),
  aboardIdCardImgPath: Yup.object().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.INDIVIDUAL && residence === ResidenceEnum.ABOARD,
    then: Yup.object().shape({
      url: Yup.string().required(),
    }),
  }),
  sendCountry: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.ABOARD && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  sendAddress: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.LOCAL && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  sendPostcode: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.LOCAL && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  sendProvince: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.LOCAL && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  sendDistrict: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.LOCAL && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  sendSubDistrict: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.LOCAL && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  aboardSendAddress: Yup.string().when(['sendResidence', 'isSameMainAddress'], {
    is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
      sendResidence === ResidenceEnum.ABOARD && !isSameMainAddress,
    then: Yup.string().required(),
  }),
  aboardSendPostcode: Yup.string().when(
    ['sendResidence', 'isSameMainAddress'],
    {
      is: (sendResidence: ResidenceEnum, isSameMainAddress: boolean) =>
        sendResidence === ResidenceEnum.ABOARD && !isSameMainAddress,
      then: Yup.string().matches(/^\d+$/).required(),
    }
  ),
  isSameMainAddress: Yup.boolean().required(),
  certificateCountry: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().required(),
  }),
  certificateAddress: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  certificatePostcode: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  certificateProvince: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  certificateDistrict: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  certificateSubDistrict: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.LOCAL,
    then: Yup.string().required(),
  }),
  aboardCertificateAddress: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().required(),
  }),
  aboardCertificatePostcode: Yup.string().when(['type', 'residence'], {
    is: (type: WriterEnum, residence: ResidenceEnum) =>
      type === WriterEnum.LEGAL && residence === ResidenceEnum.ABOARD,
    then: Yup.string().matches(/^\d+$/).required(),
  }),
  certificationTax: Yup.string().required(),
  bankAccount: Yup.object().shape({
    bankCode: Yup.string().required(),
    accountNumber: Yup.string().matches(/^\d+$/).min(10).max(10).required(),
    accountName: Yup.string().required(),
    accountType: Yup.string().required(),
    bankBranchCode: Yup.string().required(),
    bankProvince: Yup.string().required(),
    bankCoverImgPath: Yup.object().shape({
      url: Yup.string().required(),
    }),
  }),
  consents: Yup.array().of(
    Yup.object().shape({
      checked: Yup.boolean().required().oneOf([true]),
    })
  ),
  validateNumberId: Yup.boolean().when('type', {
    is: WriterEnum.INDIVIDUAL,
    then: Yup.boolean().required().oneOf([true]),
  }),
  validateTaxId: Yup.boolean().when('type', {
    is: WriterEnum.LEGAL,
    then: Yup.boolean().required().oneOf([true]),
  }),
})
